import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ChevronDown,
  ClipboardList,
  Clock,
  Compass,
  FileText,
  MessageSquare,
} from "lucide-react";
import SEO from "../components/SEO";

// Prep-page specific Alignment Check™ form (dedicated Jotform for booked leaders).
// Overrides the site-wide ALIGNMENT_CHECK_URL only on this page.
const PREP_ALIGNMENT_CHECK_URL = "https://form.jotform.com/261857097660165";

/* -------------------------------------------------------------------------
 *  Leadership Preparation — the post-Calendly bridge page.
 *
 *  Journey: Website CTA → Calendly booking → this page → Alignment Check™
 *           → Report preparation → Leadership Strategy Session™.
 *  The Calendly event must be configured to redirect to /leadership-preparation
 *  after a successful booking. This page is intentionally quiet, executive,
 *  and premium — not a generic confirmation.
 * ------------------------------------------------------------------------- */

const STEPS = [
  {
    n: "01",
    icon: ClipboardList,
    title: "Complete the Leadership Alignment Check\u2122",
    body:
      "Share your perspective on visibility, ownership, follow-through, consistency, and organizational learning.",
  },
  {
    n: "02",
    icon: FileText,
    title: "Receive Your Leadership Alignment Report\u2122",
    body:
      "After completing the check, you\u2019ll receive a professionally formatted PDF by email. The report is designed to encourage thoughtful reflection before our conversation.",
  },
  {
    n: "03",
    icon: MessageSquare,
    title: "Join Your Leadership Strategy Session\u2122",
    body:
      "Together, we\u2019ll discuss your leadership priorities, possible execution patterns, and opportunities to strengthen consistency. This is not a generic software demonstration. It is a collaborative leadership conversation.",
  },
];

const FAQ_ITEMS = [
  {
    q: "Is this required before the session?",
    a: "It is strongly recommended. Completing the Leadership Alignment Check\u2122 helps us make the conversation more useful, focused, and personalized.",
  },
  {
    q: "How long does it take?",
    a: "Most leaders complete it in 8\u201310 minutes.",
  },
  {
    q: "Will I receive my results?",
    a: "Yes. You will receive a professionally formatted Leadership Alignment Report\u2122 by email.",
  },
  {
    q: "Is this a software demo?",
    a: "Not primarily. We may show relevant parts of the platform, but the purpose of the session is to understand your priorities and explore whether AlignED can help strengthen leadership systems and consistent execution.",
  },
];

function PrepFaqItem({ item, index }) {
  const [open, setOpen] = useState(false);
  return (
    <li data-testid={`prep-faq-item-${index}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left py-6 flex items-start justify-between gap-6 group"
        aria-expanded={open}
        data-testid={`prep-faq-trigger-${index}`}
      >
        <span className="font-serif-display text-xl text-[#0A192F] leading-snug group-hover:text-[#112240] transition-colors">
          {item.q}
        </span>
        <ChevronDown
          className={`w-5 h-5 mt-1 flex-shrink-0 text-[#52796F] transition-transform ${open ? "rotate-180" : ""}`}
          strokeWidth={1.5}
        />
      </button>
      {open && (
        <div
          className="pb-6 text-[#4A5568] text-lg leading-relaxed max-w-3xl"
          data-testid={`prep-faq-answer-${index}`}
        >
          {item.a}
        </div>
      )}
    </li>
  );
}

export default function LeadershipPreparation() {
  return (
    <>
      <SEO
        title="Your Leadership Strategy Session Begins Now | AlignED"
        description="Preparation for your Leadership Strategy Session™ with AlignED. Complete the Leadership Alignment Check™ before we meet — 8 to 10 minutes of thoughtful reflection."
      />

      {/* SECTION 1 — HERO */}
      <section
        className="bg-[#FDFBF7] border-b border-[#0A192F]/10"
        data-testid="prep-hero"
      >
        <div className="max-w-4xl mx-auto px-6 lg:px-8 pt-20 pb-20 lg:pt-28 lg:pb-24">
          <div className="text-xs uppercase tracking-[0.25em] text-[#D4AF37]">
            Preparation &middot; Confirmed Booking
          </div>
          <h1 className="mt-6 font-serif-display text-4xl sm:text-5xl lg:text-[3.75rem] leading-[1.05] text-[#0A192F]">
            Your Leadership Strategy Session&trade; Begins Now.
          </h1>
          <div className="mt-8 space-y-5 text-lg text-[#4A5568] leading-relaxed max-w-3xl">
            <p>Thank you for scheduling time with AlignED.</p>
            <p>
              Our goal is to make this one of the most valuable leadership conversations you have this year.
            </p>
            <p className="font-serif-display text-xl text-[#0A192F] leading-snug">
              To do that, we begin preparing before we ever meet.
            </p>
          </div>
          <div className="mt-10">
            <a
              href={PREP_ALIGNMENT_CHECK_URL}
              target="_blank"
              rel="noreferrer"
              className="inline-flex justify-center items-center gap-2 bg-[#0A192F] text-[#FDFBF7] px-8 py-3.5 text-sm tracking-wide hover:bg-[#112240] transition-colors border border-[#0A192F]"
              data-testid="prep-hero-alignment-check-btn"
            >
              <ClipboardList className="w-4 h-4" strokeWidth={1.5} />
              Complete the Leadership Alignment Check&trade;
            </a>
            {/* Quiet time cue — no urgency, just a calm signal */}
            <div
              className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#52796F]"
              data-testid="prep-hero-time-cue"
            >
              <Clock className="w-3.5 h-3.5" strokeWidth={1.5} />
              <span>8&ndash;10 minutes</span>
              <span className="text-[#52796F]/50">&middot;</span>
              <span>Complete before your session</span>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 2 — WHY THIS MATTERS */}
      <section className="py-24 bg-[#F4F1EB]" data-testid="prep-why">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="text-xs uppercase tracking-[0.2em] text-[#52796F]">
            Why This Matters
          </div>
          <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#0A192F] leading-tight max-w-3xl">
            Better preparation creates better conversations.
          </h2>
          <div className="mt-10 space-y-6 text-lg text-[#4A5568] leading-relaxed max-w-3xl">
            <p>
              The Leadership Alignment Check&trade; is a brief reflection designed to help us understand your priorities, current realities, and opportunities for stronger organizational alignment.
            </p>
            <p className="font-serif-display text-xl text-[#52796F] leading-snug">
              It takes approximately 8&ndash;10 minutes.
            </p>
            <p>
              Your responses help us tailor the conversation to your organization so we can spend less time gathering basic information and more time discussing what matters most.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 3 — WHAT HAPPENS NEXT */}
      <section className="py-24 bg-[#FDFBF7]" data-testid="prep-next">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-[0.2em] text-[#52796F]">
              What Happens Next
            </div>
            <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#0A192F] leading-tight">
              A simple, thoughtful path to the conversation.
            </h2>
          </div>

          <ol className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
            {STEPS.map(({ n, icon: Icon, title, body }, i) => (
              <li
                key={n}
                className="relative bg-[#F4F1EB]/60 border border-[#0A192F]/10 p-8 pt-10"
                data-testid={`prep-step-${n}`}
              >
                <span className="absolute -top-4 left-6 inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#FDFBF7] border-2 border-[#38BDF8] text-[#0A192F] font-serif-display text-base">
                  {n}
                </span>
                <Icon className="w-6 h-6 text-[#52796F]" strokeWidth={1.5} />
                <h3 className="mt-5 font-serif-display text-xl text-[#0A192F] leading-snug">
                  {title}
                </h3>
                <p className="mt-4 text-[#4A5568] leading-relaxed">{body}</p>
                {i === 0 && (
                  <a
                    href={PREP_ALIGNMENT_CHECK_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-6 inline-flex items-center gap-2 text-[#0A192F] text-sm tracking-wide border-b border-[#0A192F] pb-1 hover:text-[#52796F] hover:border-[#52796F] transition-colors"
                    data-testid="prep-step-01-cta"
                  >
                    Begin the Alignment Check&trade;
                    <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                  </a>
                )}
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* SECTION 4 — OUR COMMITMENT */}
      <section
        className="py-24 bg-[#0A192F] text-[#FDFBF7]"
        data-testid="prep-commitment"
      >
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="text-xs uppercase tracking-[0.25em] text-[#D4AF37]">
            Our Commitment
          </div>
          <h2 className="mt-6 font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-tight">
            Our commitment to every leadership conversation.
          </h2>
          <div className="mt-10 space-y-6 text-lg text-[#FDFBF7]/85 leading-relaxed max-w-3xl">
            <p>
              Educational leaders deserve thoughtful conversations&mdash;not generic presentations.
            </p>
            <p>
              Before every Leadership Strategy Session&trade;, we review your Leadership Alignment Check&trade; responses and prepare specifically for your leadership priorities.
            </p>
            <p className="font-serif-display text-xl sm:text-2xl text-[#FDFBF7] leading-snug pt-2">
              Whether or not we ultimately work together, our commitment is simple:
            </p>
            <p className="font-serif-display text-xl sm:text-2xl text-[#D4AF37] leading-snug">
              You should leave the conversation with greater clarity than when you entered it.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 5 — FAQ */}
      <section className="py-24 bg-[#F4F1EB]" data-testid="prep-faq">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="text-xs uppercase tracking-[0.2em] text-[#52796F]">
            Frequently Asked
          </div>
          <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#0A192F] leading-tight">
            Common questions before the session.
          </h2>
          <ul className="mt-12 divide-y divide-[#0A192F]/10 border-t border-b border-[#0A192F]/10">
            {FAQ_ITEMS.map((item, i) => (
              <PrepFaqItem key={item.q} item={item} index={i} />
            ))}
          </ul>
        </div>
      </section>

      {/* SECTION 6 — QUIET FINAL INVITATION */}
      <section className="py-24 bg-[#FDFBF7]" data-testid="prep-final">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <Compass className="w-8 h-8 text-[#52796F] mx-auto" strokeWidth={1.25} />
          <h2 className="mt-6 font-serif-display text-3xl sm:text-4xl text-[#0A192F] leading-snug">
            Prepare for Your Leadership Strategy Session&trade;.
          </h2>
          <p className="mt-6 text-base text-[#4A5568] leading-relaxed">
            Take 8&ndash;10 minutes now, and let&apos;s make our conversation count.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={PREP_ALIGNMENT_CHECK_URL}
              target="_blank"
              rel="noreferrer"
              className="inline-flex justify-center items-center gap-2 bg-[#0A192F] text-[#FDFBF7] px-8 py-3.5 text-sm tracking-wide hover:bg-[#112240] transition-colors border border-[#0A192F]"
              data-testid="prep-final-alignment-check-btn"
            >
              <ClipboardList className="w-4 h-4" strokeWidth={1.5} />
              Complete the Leadership Alignment Check&trade;
            </a>
            <Link
              to="/insights"
              className="inline-flex justify-center items-center gap-2 text-[#0A192F] text-sm tracking-wide border-b border-[#0A192F] pb-1 self-start sm:self-center hover:text-[#52796F] hover:border-[#52796F] transition-colors"
              data-testid="prep-final-insights-link"
            >
              Explore Insights while you prepare
              <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
